import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Shield, Plus, Loader2, ArrowLeft, Users, MessageSquare, Building2, MailCheck } from "lucide-react";
import { toast } from "sonner";

export default function SuperAdmin() {
  const [allowed, setAllowed] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState({ email: "", workspace_name: "", owner_name: "" });
  const [inviting, setInviting] = useState(false);

  const load = useCallback(async () => {
    const [w, s] = await Promise.all([api.get("/admin/workspaces"), api.get("/admin/stats")]);
    setWorkspaces(w.data);
    setStats(s.data);
  }, []);

  useEffect(() => {
    api.get("/admin/me")
      .then((r) => {
        setAllowed(r.data.super_admin);
        if (r.data.super_admin) load();
      })
      .catch(() => setAllowed(false));
  }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      const { data } = await api.post("/admin/workspaces/invite", form);
      toast[data.email_sent ? "success" : "warning"](
        data.email_sent ? `Invitaţie trimisă către ${form.email}` : data.note);
      setForm({ email: "", workspace_name: "", owner_name: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invitaţia a eşuat");
    } finally { setInviting(false); }
  };

  if (allowed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg text-fg">
        <div className="flex items-center gap-3 text-sm label-tiny">
          <div className="w-2 h-2 bg-brand pulse-amber" /> se verifică accesul…
        </div>
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg text-fg p-6">
        <div className="text-center space-y-3">
          <Shield className="w-8 h-8 text-fg-muted mx-auto" />
          <p className="text-sm">Zona asta e doar pentru super-admini.</p>
          <a href="/" className="text-xs text-brand-2 hover:text-brand">Înapoi la Qedara Chat</a>
        </div>
      </div>
    );
  }

  const TILES = stats ? [
    [Building2, "Workspace-uri", stats.workspaces],
    [Users, "Utilizatori", stats.users],
    [MessageSquare, "Mesaje", stats.messages],
    [MailCheck, "Invitaţii în aşteptare", stats.pending_invitations],
  ] : [];

  return (
    <div className="min-h-screen bg-bg text-fg" data-testid="super-admin">
      <div className="h-14 px-6 flex items-center gap-2 border-b border-line/10">
        <Shield className="w-4 h-4 text-brand" />
        <h1 className="font-bold tracking-tight">Super-admin</h1>
        <a href="/" className="ml-auto text-xs text-fg-muted hover:text-fg flex items-center gap-1.5">
          <ArrowLeft className="w-3.5 h-3.5" /> Qedara Chat
        </a>
      </div>

      <div className="p-6 max-w-5xl space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {TILES.map(([Icon, label, value]) => (
            <div key={label} className="bg-surface border border-line/10 p-4">
              <div className="flex items-center gap-2 mb-1">
                <Icon className="w-3.5 h-3.5 text-brand" />
                <span className="label-tiny">{label}</span>
              </div>
              <p className="text-2xl font-bold tracking-tight">{value}</p>
            </div>
          ))}
        </div>

        <section className="bg-surface border border-line/10">
          <div className="px-5 py-4 border-b border-line/10">
            <h2 className="font-bold tracking-tight">Invită un workspace nou</h2>
          </div>
          <form onSubmit={invite} className="p-5 grid sm:grid-cols-4 gap-3 items-end">
            <div className="sm:col-span-1">
              <label htmlFor="inv-owner" className="label-tiny block mb-1.5">Nume proprietar</label>
              <input id="inv-owner" data-testid="invite-owner" required value={form.owner_name}
                     onChange={(e) => setForm({ ...form, owner_name: e.target.value })}
                     className="input-flat text-sm w-full" />
            </div>
            <div className="sm:col-span-1">
              <label htmlFor="inv-ws" className="label-tiny block mb-1.5">Nume workspace</label>
              <input id="inv-ws" data-testid="invite-workspace" required value={form.workspace_name}
                     onChange={(e) => setForm({ ...form, workspace_name: e.target.value })}
                     className="input-flat text-sm w-full" />
            </div>
            <div className="sm:col-span-1">
              <label htmlFor="inv-email" className="label-tiny block mb-1.5">Email</label>
              <input id="inv-email" data-testid="invite-email" type="email" required value={form.email}
                     onChange={(e) => setForm({ ...form, email: e.target.value })}
                     className="input-flat text-sm mono w-full" />
            </div>
            <button type="submit" data-testid="invite-submit" disabled={inviting}
                    className="btn-primary text-sm flex items-center justify-center gap-1.5 disabled:opacity-50">
              {inviting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Invită
            </button>
          </form>
        </section>

        <section className="bg-surface border border-line/10">
          <div className="px-5 py-4 border-b border-line/10 flex items-center justify-between">
            <h2 className="font-bold tracking-tight">Workspace-uri</h2>
            <span className="label-tiny">// {workspaces.length}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left label-tiny border-b border-line/10">
                  {["Nume", "Subdomeniu", "Owner", "Membri", "Plan", "Creat"].map((h) => (
                    <th key={h} className="px-5 py-2 font-normal">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-line/5">
                {workspaces.map((w) => (
                  <tr key={w.id} data-testid={`ws-row-${w.id}`} className={w.suspended ? "opacity-50" : ""}>
                    <td className="px-5 py-2.5">{w.name}</td>
                    <td className="px-5 py-2.5 mono text-xs text-fg-muted">{w.subdomain || "—"}</td>
                    <td className="px-5 py-2.5 text-xs">
                      {w.owner_name}<span className="block text-fg-muted mono">{w.owner_email}</span>
                    </td>
                    <td className="px-5 py-2.5">{w.members}</td>
                    <td className="px-5 py-2.5 label-tiny uppercase">{w.plan}</td>
                    <td className="px-5 py-2.5 text-xs text-fg-muted mono">
                      {(w.created_at || "").slice(0, 10)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
